import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import Seo from "@/components/feature/Seo";
import { siteInfo, stats, testimonials } from "@/mocks/news";

const principles = [
  {
    icon: "ri-bookmark-3-line",
    title: "标明来源",
    text: "每一条内容都尽可能注明是官方公告、公开路透还是网友整理，让你清楚知道信息的分量。",
  },
  {
    icon: "ri-scales-3-line",
    title: "传闻与事实分开",
    text: "未经证实的说法会明确标注为「尚待确认」，不会用肯定的语气写成结论。",
  },
  {
    icon: "ri-refresh-line",
    title: "有进展就更新",
    text: "当事方给出回应或原始信息被证伪时，我们会更新或撤下内容，避免误导读者。",
  },
  {
    icon: "ri-emotion-happy-line",
    title: "克制表达",
    text: "不做标题党，不放大情绪。把一件事讲清楚，比把一句话喊得更响更有价值。",
  },
];

export default function About() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于我们 - 看瓜爆料",
      url: `${siteInfo.domain}/about`,
      description: siteInfo.description,
      inLanguage: "zh-CN",
      mainEntity: {
        "@type": "Organization",
        name: siteInfo.name,
        url: siteInfo.domain,
        email: siteInfo.contactEmail,
        description: siteInfo.description,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: siteInfo.domain },
        { "@type": "ListItem", position: 2, name: "关于我们", item: `${siteInfo.domain}/about` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Seo
        title="关于我们 - 看瓜爆料品牌介绍与内容原则"
        description="看瓜爆料是一个专注娱乐热点的资讯门户，坚持标明来源、区分传闻与事实、有进展及时更新。了解我们的品牌定位、内容原则与联系方式。"
        keywords="看瓜爆料,关于看瓜爆料,看瓜爆料内容原则,娱乐热点资讯,实时爆料"
        path="/about"
        jsonLd={jsonLd}
      />
      <Navbar />

      <main>
        <nav aria-label="面包屑" className="border-b border-background-200 bg-background-100">
          <ol className="mx-auto flex max-w-site flex-wrap items-center gap-2 px-4 py-3 text-xs text-foreground-600 md:px-6">
            <li>
              <Link to="/" className="transition-colors hover:text-primary-600">
                首页
              </Link>
            </li>
            <li aria-hidden="true" className="text-foreground-400">/</li>
            <li className="font-medium text-foreground-900">关于我们</li>
          </ol>
        </nav>

        <section className="border-b border-background-200 bg-background-50">
          <div className="mx-auto max-w-site px-4 py-14 md:px-6 md:py-20">
            <p className="text-xs font-medium tracking-[0.2em] text-primary-600">ABOUT KANGUA</p>
            <h1 className="mt-3 max-w-3xl font-heading text-3xl font-black leading-tight text-foreground-950 md:text-5xl">
              把一件热闹的事，讲得清楚一点
            </h1>
            <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-700 md:text-base">
              看瓜爆料是一个专注娱乐热点的资讯门户。我们不做夸张的标题党，而是把散落各处的公开信息按照时间线整理清楚，
              让你在几分钟内就能弄明白一件事的来龙去脉，再决定要不要继续关注。
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-site px-4 py-14 md:px-6 md:py-16" aria-labelledby="position-title">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <h2 id="position-title" className="font-heading text-2xl font-black text-foreground-950">
                网站定位
              </h2>
              <div className="mt-5 space-y-4 text-sm leading-relaxed text-foreground-700">
                <p>
                  我们服务的读者，是那群希望「第一时间知道进展，但又不想被情绪牵着走」的人。
                  他们关心的不是耸动的标题，而是这件事到底发生到哪一步、各方说了什么、还有哪些地方没搞清楚。
                </p>
                <p>
                  因此，看瓜爆料把重心放在两件事上：一是更新速度，热点时段保持高频轮值；二是信息结构，
                  每条内容都会交代清楚时间、来源与当前状态，方便你快速建立判断。
                </p>
                <p>
                  热榜只反映当下的讨论热度，不代表我们对事件本身的立场。评论区里的观点属于读者个人表达，
                  与网站的叙述保持距离。
                </p>
              </div>
            </div>

            <div className="lg:col-span-5">
              <dl className="grid grid-cols-2 gap-4">
                {stats.map((item) => (
                  <div
                    key={item.label}
                    className="rounded-lg border border-background-200 bg-background-100 p-5"
                  >
                    <dt className="text-xs text-foreground-500">{item.label}</dt>
                    <dd className="mt-2 font-heading text-2xl font-black text-primary-600">
                      {item.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </section>

        <section className="border-y border-background-200 bg-background-100">
          <div className="mx-auto max-w-site px-4 py-14 md:px-6 md:py-16" aria-labelledby="principles-title">
            <div className="mb-8">
              <p className="text-xs font-medium tracking-[0.2em] text-primary-600">OUR PRINCIPLES</p>
              <h2 id="principles-title" className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                内容原则
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {principles.map((item, index) => (
                <article
                  key={item.title}
                  className={`rounded-lg border p-5 ${
                    index === 0 ? "border-primary-200 bg-primary-50" : "border-background-200 bg-background-50"
                  }`}
                >
                  <span
                    className={`flex h-11 w-11 items-center justify-center rounded-md ${
                      index === 0 ? "bg-primary-500 text-background-50" : "bg-accent-100 text-accent-800"
                    }`}
                  >
                    <i className={`${item.icon} text-xl`} aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-site px-4 py-14 md:px-6 md:py-16" aria-labelledby="reputation-title">
          <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-xs font-medium tracking-[0.2em] text-primary-600">READERS</p>
              <h2 id="reputation-title" className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                读者怎么说
              </h2>
            </div>
            <span className="text-xs text-foreground-500">内容来自读者反馈整理</span>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {testimonials.map((item, index) => (
              <figure
                key={item.name}
                className={`rounded-lg border border-background-200 p-5 ${
                  index % 2 === 1 ? "bg-secondary-50" : "bg-background-50"
                }`}
              >
                <div className="flex items-center gap-1 text-secondary-600">
                  {[0, 1, 2, 3, 4].map((star) => (
                    <i key={star} className="ri-star-fill text-sm" aria-hidden="true"></i>
                  ))}
                </div>
                <blockquote className="mt-3 text-sm leading-relaxed text-foreground-700">
                  {item.text}
                </blockquote>
                <figcaption className="mt-4 text-xs">
                  <span className="block font-medium text-foreground-900">{item.name}</span>
                  <span className="text-foreground-500">{item.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="border-t border-background-200 bg-background-100" aria-labelledby="contact-title">
          <div className="mx-auto grid max-w-site grid-cols-1 gap-8 px-4 py-14 md:px-6 md:py-16 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <h2 id="contact-title" className="font-heading text-2xl font-black text-foreground-950">
                联系我们
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                无论你是想提供一条线索，还是对内容有疑问，都欢迎通过下面的邮箱联系我们。
                提交线索时，如果能附上可核实的公开信息，会大大加快我们的核实速度。
              </p>
              <Link
                to="/"
                className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-foreground-950 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-flashlight-line" aria-hidden="true"></i>
                去看看今日速递
              </Link>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-7">
              <a
                href={`mailto:${siteInfo.contactEmail}`}
                className="group rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-mail-line text-xl" aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                  读者与线索
                </h3>
                <p className="mt-1.5 text-sm text-foreground-600 transition-colors group-hover:text-primary-700">
                  {siteInfo.contactEmail}
                </p>
              </a>
              <a
                href={`mailto:${siteInfo.prEmail}`}
                className="group rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-accent-300"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className="ri-briefcase-line text-xl" aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                  商务与合作
                </h3>
                <p className="mt-1.5 text-sm text-foreground-600 transition-colors group-hover:text-accent-700">
                  {siteInfo.prEmail}
                </p>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
}